/**
 * Embeddable Booking Widget for Vacation Rentals
 * Estate Flow - Sebastian Alliance Group LLC
 * 
 * This component renders a fully functional booking widget that can be embedded
 * on third-party websites via iframe. It shows all properties for a specific owner
 * and allows complete booking flow within the iframe.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Calendar, Search, Users, MapPin, Bed, Bath, Star, ChevronLeft,
  ChevronRight, Check, CreditCard, Shield, Percent, Home, X, Loader2,
  CalendarDays, Filter, Grid, List
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRODUCTION_DOMAIN = 'https://apcestateflow.com';

// Date utilities
const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const getDaysBetween = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
};

const EmbedBookingWidget = () => {
  const { ownerId } = useParams();
  const [searchParams] = useSearchParams();
  
  // Get customization from URL params
  const primaryColor = searchParams.get('color') || '#10B981';
  const accentColor = searchParams.get('accent') || '#059669';
  const showFilters = searchParams.get('filters') !== 'false';
  
  // State
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [ownerInfo, setOwnerInfo] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [bookingStep, setBookingStep] = useState('search'); // search, property, dates, guest, payment, confirmation
  const [viewMode, setViewMode] = useState('grid');
  
  // Search/Filter state
  const [filters, setFilters] = useState({
    location: '',
    checkIn: '',
    checkOut: '',
    guests: 2,
    minPrice: 0,
    maxPrice: 1000,
    bedrooms: 0,
    propertyType: ''
  });
  
  // Booking state
  const [bookingData, setBookingData] = useState({
    checkIn: '',
    checkOut: '',
    guests: 2,
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    specialRequests: ''
  });
  
  const [pricing, setPricing] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Fetch owner's properties
  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/embed/widget/${ownerId}/properties`);
      setProperties(response.data.properties || []);
      setOwnerInfo(response.data.owner || {});
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Unable to load properties');
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    if (ownerId) {
      fetchProperties();
    }
  }, [ownerId, fetchProperties]);

  // Filter properties based on search criteria
  const filteredProperties = properties.filter(property => {
    if (filters.location && !property.city?.toLowerCase().includes(filters.location.toLowerCase()) &&
        !property.country?.toLowerCase().includes(filters.location.toLowerCase())) {
      return false;
    }
    if (filters.guests > property.max_guests) return false;
    if (filters.bedrooms > 0 && property.bedrooms < filters.bedrooms) return false;
    if (filters.propertyType && property.property_type !== filters.propertyType) return false;
    if (property.base_nightly_rate < filters.minPrice || property.base_nightly_rate > filters.maxPrice) return false;
    return true;
  });

  // Calculate pricing when dates change
  const calculatePricing = async (property, checkIn, checkOut) => {
    if (!checkIn || !checkOut) return null;
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/vacation/properties/${property.id}/pricing`,
        { params: { check_in: checkIn, check_out: checkOut } }
      );
      return response.data;
    } catch (error) {
      console.error('Error calculating pricing:', error);
      // Fallback calculation
      const nights = getDaysBetween(checkIn, checkOut);
      const subtotal = property.base_nightly_rate * nights;
      const discount = subtotal * (property.direct_booking_discount || 10) / 100;
      const cleaningFee = property.cleaning_fee || 0;
      const serviceFee = (subtotal - discount + cleaningFee) * 0.02;
      
      return {
        nights,
        nightly_rate: property.base_nightly_rate,
        subtotal,
        direct_booking_discount: discount,
        cleaning_fee: cleaningFee,
        service_fee: serviceFee,
        total_with_discount: subtotal - discount + cleaningFee + serviceFee,
        you_save: discount
      };
    }
  };

  // Handle property selection
  const handleSelectProperty = async (property) => {
    setSelectedProperty(property);
    setBookingStep('dates');
    
    // If dates already selected, calculate pricing
    if (bookingData.checkIn && bookingData.checkOut) {
      const pricingData = await calculatePricing(property, bookingData.checkIn, bookingData.checkOut);
      setPricing(pricingData);
    }
  };

  // Handle date selection
  const handleDateChange = async (field, value) => {
    const newBookingData = { ...bookingData, [field]: value };
    setBookingData(newBookingData);
    
    if (newBookingData.checkIn && newBookingData.checkOut && selectedProperty) {
      const pricingData = await calculatePricing(selectedProperty, newBookingData.checkIn, newBookingData.checkOut);
      setPricing(pricingData);
    }
  };

  // Process booking
  const handleBooking = async () => {
    if (!selectedProperty || !pricing) return;
    
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/embed/widget/${ownerId}/book`, {
        property_id: selectedProperty.id,
        check_in: bookingData.checkIn,
        check_out: bookingData.checkOut,
        guests: bookingData.guests,
        guest_name: bookingData.guestName,
        guest_email: bookingData.guestEmail,
        guest_phone: bookingData.guestPhone,
        special_requests: bookingData.specialRequests,
        total_amount: pricing.total_with_discount,
        discount_amount: pricing.you_save
      });
      
      setBookingResult(response.data);
      setBookingStep('confirmation');
      toast.success('Booking confirmed!');
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Booking failed. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  // Reset to search
  const resetBooking = () => {
    setSelectedProperty(null);
    setBookingStep('search');
    setPricing(null);
    setBookingResult(null);
    setBookingData({
      checkIn: '',
      checkOut: '',
      guests: 2,
      guestName: '',
      guestEmail: '',
      guestPhone: '',
      specialRequests: ''
    });
  };

  // Custom styles based on color
  const customStyles = {
    '--primary-color': primaryColor,
    '--accent-color': accentColor,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" style={customStyles}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: primaryColor }} />
          <p className="text-gray-600">Loading properties...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={customStyles}>
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {bookingStep !== 'search' && (
                <Button variant="ghost" size="sm" onClick={resetBooking}>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              )}
              <h1 className="text-lg font-semibold text-gray-900">
                {ownerInfo?.business_name || 'Vacation Rentals'}
              </h1>
            </div>
            <Badge className="text-white" style={{ backgroundColor: primaryColor }}>
              <Percent className="h-3 w-3 mr-1" />
              Book Direct & Save
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Search Step */}
        {bookingStep === 'search' && (
          <div className="space-y-6">
            {/* Search Filters */}
            {showFilters && (
              <Card>
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                      <Label className="text-sm">Location</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Where to?"
                          className="pl-10"
                          value={filters.location}
                          onChange={(e) => setFilters({...filters, location: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">Check In</Label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          type="date"
                          className="pl-10"
                          min={new Date().toISOString().split('T')[0]}
                          value={filters.checkIn}
                          onChange={(e) => {
                            setFilters({...filters, checkIn: e.target.value});
                            setBookingData({...bookingData, checkIn: e.target.value});
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">Check Out</Label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          type="date"
                          className="pl-10"
                          min={filters.checkIn || new Date().toISOString().split('T')[0]}
                          value={filters.checkOut}
                          onChange={(e) => {
                            setFilters({...filters, checkOut: e.target.value});
                            setBookingData({...bookingData, checkOut: e.target.value});
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">Guests</Label>
                      <div className="relative">
                        <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <select
                          className="w-full pl-10 pr-3 py-2 border rounded-md"
                          value={filters.guests}
                          onChange={(e) => {
                            setFilters({...filters, guests: parseInt(e.target.value)});
                            setBookingData({...bookingData, guests: parseInt(e.target.value)});
                          }}
                        >
                          {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                            <option key={n} value={n}>{n} Guest{n > 1 ? 's' : ''}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex items-end">
                      <Button 
                        className="w-full text-white"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <Search className="h-4 w-4 mr-2" />
                        Search
                      </Button>
                    </div>
                  </div>
                  
                  {/* Additional Filters */}
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                    <select
                      className="px-3 py-1.5 text-sm border rounded-md"
                      value={filters.bedrooms}
                      onChange={(e) => setFilters({...filters, bedrooms: parseInt(e.target.value)})}
                    >
                      <option value="0">Any Bedrooms</option>
                      {[1,2,3,4,5,6].map(n => (
                        <option key={n} value={n}>{n}+ Bedrooms</option>
                      ))}
                    </select>
                    <select
                      className="px-3 py-1.5 text-sm border rounded-md"
                      value={filters.propertyType}
                      onChange={(e) => setFilters({...filters, propertyType: e.target.value})}
                    >
                      <option value="">All Property Types</option>
                      <option value="villa">Villa</option>
                      <option value="condo">Condo</option>
                      <option value="apartment">Apartment</option>
                      <option value="house">House</option>
                      <option value="beachfront">Beachfront</option>
                    </select>
                    <div className="flex items-center gap-2 ml-auto">
                      <Button
                        variant={viewMode === 'grid' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setViewMode('grid')}
                      >
                        <Grid className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={viewMode === 'list' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setViewMode('list')}
                      >
                        <List className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Properties Grid */}
            <div className="mb-4">
              <p className="text-gray-600">{filteredProperties.length} properties available</p>
            </div>
            
            {filteredProperties.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Home className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No properties found</h3>
                  <p className="text-gray-500">Try adjusting your search filters</p>
                </CardContent>
              </Card>
            ) : (
              <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
                {filteredProperties.map((property) => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    viewMode={viewMode}
                    primaryColor={primaryColor}
                    onSelect={() => handleSelectProperty(property)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Date Selection Step */}
        {bookingStep === 'dates' && selectedProperty && (
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-6">
              <PropertyDetailCard property={selectedProperty} primaryColor={primaryColor} />
              
              {/* Calendar / Date Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarDays className="h-5 w-5" />
                    Select Your Dates
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Check In</Label>
                      <Input
                        type="date"
                        min={new Date().toISOString().split('T')[0]}
                        value={bookingData.checkIn}
                        onChange={(e) => handleDateChange('checkIn', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Check Out</Label>
                      <Input
                        type="date"
                        min={bookingData.checkIn || new Date().toISOString().split('T')[0]}
                        value={bookingData.checkOut}
                        onChange={(e) => handleDateChange('checkOut', e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Guests</Label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={bookingData.guests}
                      onChange={(e) => setBookingData({...bookingData, guests: parseInt(e.target.value)})}
                    >
                      {Array.from({length: selectedProperty.max_guests}, (_, i) => i + 1).map(n => (
                        <option key={n} value={n}>{n} Guest{n > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                  </div>
                  
                  {bookingData.checkIn && bookingData.checkOut && (
                    <Button 
                      className="w-full text-white"
                      style={{ backgroundColor: primaryColor }}
                      onClick={() => setBookingStep('guest')}
                      disabled={!pricing}
                    >
                      Continue to Guest Details
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Pricing Summary */}
            <div>
              <PricingSummary 
                pricing={pricing} 
                property={selectedProperty}
                bookingData={bookingData}
                primaryColor={primaryColor}
              />
            </div>
          </div>
        )}

        {/* Guest Details Step */}
        {bookingStep === 'guest' && selectedProperty && (
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Guest Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input
                        placeholder="John Doe"
                        value={bookingData.guestName}
                        onChange={(e) => setBookingData({...bookingData, guestName: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        placeholder="john@example.com"
                        value={bookingData.guestEmail}
                        onChange={(e) => setBookingData({...bookingData, guestEmail: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Phone Number</Label>
                    <Input
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      value={bookingData.guestPhone}
                      onChange={(e) => setBookingData({...bookingData, guestPhone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Special Requests (Optional)</Label>
                    <textarea
                      className="w-full px-3 py-2 border rounded-md"
                      rows={3}
                      placeholder="Early check-in, dietary requirements, etc."
                      value={bookingData.specialRequests}
                      onChange={(e) => setBookingData({...bookingData, specialRequests: e.target.value})}
                    />
                  </div>
                  
                  <Button 
                    className="w-full text-white"
                    style={{ backgroundColor: primaryColor }}
                    onClick={() => setBookingStep('payment')}
                    disabled={!bookingData.guestName || !bookingData.guestEmail}
                  >
                    Continue to Payment
                  </Button>
                </CardContent>
              </Card>
            </div>
            
            <div>
              <PricingSummary 
                pricing={pricing} 
                property={selectedProperty}
                bookingData={bookingData}
                primaryColor={primaryColor}
              />
            </div>
          </div>
        )}

        {/* Payment Step */}
        {bookingStep === 'payment' && selectedProperty && (
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Complete Your Booking
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Booking Summary */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold mb-3">Booking Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Property</span>
                        <span className="font-medium">{selectedProperty.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Check In</span>
                        <span>{formatDate(bookingData.checkIn)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Check Out</span>
                        <span>{formatDate(bookingData.checkOut)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Guests</span>
                        <span>{bookingData.guests}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Guest Name</span>
                        <span>{bookingData.guestName}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Security Notice */}
                  <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                    <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-900">Secure Booking</p>
                      <p className="text-sm text-blue-700">
                        Your booking is protected. Payment will be processed securely.
                      </p>
                    </div>
                  </div>
                  
                  {/* Savings Highlight */}
                  {pricing?.you_save > 0 && (
                    <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                      <Percent className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-green-900">You're Saving ${pricing.you_save.toFixed(2)}!</p>
                        <p className="text-sm text-green-700">
                          By booking direct, you're getting a better deal than booking through OTAs.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <Button 
                    className="w-full text-white py-6 text-lg"
                    style={{ backgroundColor: primaryColor }}
                    onClick={handleBooking}
                    disabled={processingPayment}
                  >
                    {processingPayment ? (
                      <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Processing...</>
                    ) : (
                      <><Check className="h-5 w-5 mr-2" /> Confirm Booking - ${pricing?.total_with_discount?.toFixed(2)}</>
                    )}
                  </Button>
                  
                  <p className="text-xs text-center text-gray-500">
                    By confirming, you agree to the property's cancellation policy and house rules.
                  </p>
                </CardContent>
              </Card>
            </div>
            
            <div>
              <PricingSummary 
                pricing={pricing} 
                property={selectedProperty}
                bookingData={bookingData}
                primaryColor={primaryColor}
                showDetails
              />
            </div>
          </div>
        )}

        {/* Confirmation Step */}
        {bookingStep === 'confirmation' && bookingResult && (
          <div className="max-w-2xl mx-auto">
            <Card className="text-center">
              <CardContent className="py-12">
                <div 
                  className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{ backgroundColor: `${primaryColor}20` }}
                >
                  <Check className="h-10 w-10" style={{ color: primaryColor }} />
                </div>
                
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Booking Confirmed!</h2>
                <p className="text-gray-600 mb-6">
                  Your reservation has been confirmed. A confirmation email has been sent to {bookingData.guestEmail}.
                </p>
                
                <div className="bg-gray-50 rounded-lg p-6 text-left mb-6">
                  <h3 className="font-semibold mb-4">Booking Details</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Confirmation #</span>
                      <span className="font-mono font-bold">{bookingResult.booking?.id?.substring(0, 8).toUpperCase()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Property</span>
                      <span>{selectedProperty?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Check In</span>
                      <span>{formatDate(bookingData.checkIn)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Check Out</span>
                      <span>{formatDate(bookingData.checkOut)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-3 mt-3">
                      <span className="font-semibold">Total Paid</span>
                      <span className="font-bold text-lg" style={{ color: primaryColor }}>
                        ${pricing?.total_with_discount?.toFixed(2)}
                      </span>
                    </div>
                    {pricing?.you_save > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>You Saved</span>
                        <span className="font-medium">${pricing.you_save.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <Button onClick={resetBooking} variant="outline">
                  Book Another Property
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
      
      {/* Footer */}
      <footer className="bg-white border-t mt-12 py-4">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
          Powered by <a href={PRODUCTION_DOMAIN} target="_blank" rel="noopener noreferrer" className="font-medium" style={{ color: primaryColor }}>Estate Flow</a>
        </div>
      </footer>
    </div>
  );
};

// Property Card Component
const PropertyCard = ({ property, viewMode, primaryColor, onSelect }) => {
  if (viewMode === 'list') {
    return (
      <Card className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={onSelect}>
        <div className="flex">
          <div className="w-48 h-36 bg-gradient-to-br from-emerald-400 to-blue-500 flex-shrink-0">
            {property.cover_photo ? (
              <img src={property.cover_photo} alt={property.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Home className="h-12 w-12 text-white/50" />
              </div>
            )}
          </div>
          <CardContent className="p-4 flex-1">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-lg">{property.name}</h3>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {property.city}, {property.country}
                </p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold" style={{ color: primaryColor }}>${property.base_nightly_rate}</span>
                <span className="text-gray-500">/night</span>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
              <span className="flex items-center gap-1"><Bed className="h-4 w-4" /> {property.bedrooms} bed</span>
              <span className="flex items-center gap-1"><Bath className="h-4 w-4" /> {property.bathrooms} bath</span>
              <span className="flex items-center gap-1"><Users className="h-4 w-4" /> {property.max_guests} guests</span>
            </div>
            {property.direct_booking_discount > 0 && (
              <Badge className="mt-2 bg-green-100 text-green-700">
                Save {property.direct_booking_discount}% - Book Direct!
              </Badge>
            )}
          </CardContent>
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={onSelect}>
      <div className="h-48 bg-gradient-to-br from-emerald-400 to-blue-500 relative">
        {property.cover_photo ? (
          <img src={property.cover_photo} alt={property.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Home className="h-16 w-16 text-white/50" />
          </div>
        )}
        {property.direct_booking_discount > 0 && (
          <Badge className="absolute top-3 left-3 bg-green-500 text-white">
            Save {property.direct_booking_discount}%
          </Badge>
        )}
        <Badge className="absolute top-3 right-3 bg-white text-gray-700">
          {property.property_type}
        </Badge>
      </div>
      <CardContent className="p-4">
        <h3 className="font-semibold text-lg mb-1">{property.name}</h3>
        <p className="text-sm text-gray-500 flex items-center gap-1 mb-3">
          <MapPin className="h-3 w-3" />
          {property.city}, {property.country}
        </p>
        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
          <span>{property.bedrooms} bed</span>
          <span>{property.bathrooms} bath</span>
          <span>{property.max_guests} guests</span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <span className="text-2xl font-bold" style={{ color: primaryColor }}>${property.base_nightly_rate}</span>
            <span className="text-gray-500">/night</span>
          </div>
          <Button size="sm" style={{ backgroundColor: primaryColor }} className="text-white">
            View Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Property Detail Card
const PropertyDetailCard = ({ property, primaryColor }) => (
  <Card>
    <div className="h-64 bg-gradient-to-br from-emerald-400 to-blue-500 relative">
      {property.cover_photo ? (
        <img src={property.cover_photo} alt={property.name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Home className="h-20 w-20 text-white/50" />
        </div>
      )}
    </div>
    <CardContent className="p-6">
      <h2 className="text-2xl font-bold mb-2">{property.name}</h2>
      <p className="text-gray-500 flex items-center gap-1 mb-4">
        <MapPin className="h-4 w-4" />
        {property.city}, {property.country}
      </p>
      <div className="flex flex-wrap gap-4 text-gray-600 mb-4">
        <span className="flex items-center gap-1"><Bed className="h-5 w-5" /> {property.bedrooms} Bedrooms</span>
        <span className="flex items-center gap-1"><Bath className="h-5 w-5" /> {property.bathrooms} Bathrooms</span>
        <span className="flex items-center gap-1"><Users className="h-5 w-5" /> Up to {property.max_guests} Guests</span>
      </div>
      {property.description && (
        <p className="text-gray-600">{property.description}</p>
      )}
      {property.amenities?.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <h4 className="font-semibold mb-2">Amenities</h4>
          <div className="flex flex-wrap gap-2">
            {property.amenities.map((amenity, idx) => (
              <Badge key={idx} variant="outline" className="capitalize">
                {amenity}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);

// Pricing Summary Component
const PricingSummary = ({ pricing, property, bookingData, primaryColor, showDetails }) => {
  if (!pricing) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Price Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-sm">Select dates to see pricing</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="sticky top-20">
      <CardHeader>
        <CardTitle className="text-lg">Price Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">${pricing.nightly_rate} x {pricing.nights} nights</span>
          <span>${pricing.subtotal?.toFixed(2)}</span>
        </div>
        {pricing.cleaning_fee > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Cleaning fee</span>
            <span>${pricing.cleaning_fee?.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Service fee (2%)</span>
          <span>${pricing.service_fee?.toFixed(2)}</span>
        </div>
        {pricing.you_save > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>Direct booking discount</span>
            <span>-${pricing.you_save?.toFixed(2)}</span>
          </div>
        )}
        <div className="border-t pt-3">
          <div className="flex justify-between font-bold text-lg">
            <span>Total</span>
            <span style={{ color: primaryColor }}>${pricing.total_with_discount?.toFixed(2)}</span>
          </div>
        </div>
        {pricing.you_save > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
            <p className="text-sm text-green-700 font-medium">
              🎉 You're saving ${pricing.you_save?.toFixed(2)} by booking direct!
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmbedBookingWidget;
